import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { VariantDisplayTypeValues, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TextFieldProps } from "@insite/mobius/TextField";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENProductDescription from "@tCentro/Components/CENProductDescription";
import ProductAddToCartButton from "@tCentro/Overrides/Components/ProductAddToCartButton";
import ProductPrice, { ProductPriceStyles } from "@tCentro/Overrides/Components/ProductPrice";
import ProductQuantityOrdered from "@tCentro/Overrides/Components/ProductQuantityOrdered";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPrice: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    productSettings: getSettingsCollection(state).productSettings,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
    productDescriptionWrapper?: InjectableCss;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 8px;
                        padding-top: 6px;
                    `,
                    css`
                        padding-left: 12px;
                        padding-top: 6px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
                span {
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
            `,
            typographyProps: {
                size: 20,
            },
        },
    },
    brandGridItem: {
        width: 12,
        css: css`
            padding: 2px;
        `,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            padding: 2px;
        `,
    },
    partNumbersGridItem: {
        width: 12,
    },
    swatchGridItem: {
        width: 12,
    },
    availabilityGridItem: {
        width: 12,
        css: css`
            padding: 0px !important;
            div[class^="GridItemStyle"] {
                padding: 0px !important;
            }
        `,
    },
    attributesGridItem: {
        width: 12,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    productDescriptionWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    availabilityStyles: {
        messageText: {
            css: css`
                width: 100%;
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                color: ${getProp("theme.typography.p.color")};
                ${wrapInContainerStyles}
            `,
        },
        availabilityByWarehouseLink: {
            css: css`
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                line-height: 28px;
            `,
        },
    },
    brandStyles: {
        nameLink: {
            css: css`
                width: 100%;
                span {
                    color: ${getProp("theme.typography.p.color")} !important;
                    font-weight: ${TYPOGRAPHY.weight.mediumbold} !important;
                    font-size: ${TYPOGRAPHY.size.smaller};
                    line-height: 16px !important;
                }
            `,
        },
    },
};

const styles = productInformationStyles;

const NISProductListProductInformation: FC<Props> = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    productSettings,
    showPrice,
}) => {
    const { product } = productContext;
    let variantDisplayTrait = null;

    if (!product) {
        return null;
    }

    if (product?.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    return (
        <GridContainer {...styles.container}>
            {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />
                </GridItem>
            )}
            {showPartNumbers && (
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.partNumbersStyles}
                    />
                </GridItem>
            )}
            {/* {variantDisplayTrait && (
                <GridItem {...styles.swatchGridItem}>
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </GridItem>
            )} */}
            {showAttributes && (
                <GridItem {...styles.attributesGridItem}>
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )}
            <StyledWrapper {...styles.productDescriptionWrapper}>
                <CENProductDescription product={productContext} />
            </StyledWrapper>
            {settingsCollection.productSettings.showInventoryAvailability && showAvailability && (
                <GridItem {...styles.availabilityGridItem}>
                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(NISProductListProductInformation));
